import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";

interface Review {
  id: number;
  stars: number;
  title: string;
  description: string;
}

export const ReviewSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isManualScrollRef = useRef(false);

  const reviews: Review[] = [
    {
      id: 1,
      stars: 5,
      title: "Like Uber for Party Buses",
      description: "We booked Disco for my best friend's birthday and it was next-level. The bus pulled up right on time, the driver was super chill, and the lights + sound system made it feel like a club on wheels. Can't wait to book again!"
    },
    {
      id: 2,
      stars: 5,
      title: "Seamless from start to finish",
      description: "The app made it so easy to book, track, and even customize our ride. Our driver was amazing, the bus was spotless, and we never had to worry about parking or logistics. Total game-changer for big nights out."
    },
    {
      id: 3,
      stars: 5,
      title: "Bachelor party perfection",
      description: "Disco delivered. We hit multiple spots without ever needing to deal with cabs or Ubers, and the vibe on the bus was better than some of the clubs we went to. The lighting, music, and energy were all 🔥."
    },
    {
      id: 4,
      stars: 5,
      title: "Prom night MVP!",
      description: "We used Disco for our prom group and it was the highlight of the night. Parents loved the safety aspect, we loved the full-on party experience. The driver was super professional and we felt like VIPs all night."
    },
    {
      id: 5,
      stars: 5,
      title: "The best way to do Vegas",
      description: "Booked a Disco bus for a Vegas weekend and WOW. We didn't wait in lines, the ride itself was part of the fun, and the whole crew was obsessed. Booking was easy and the driver even helped us plan our route."
    },
    {
      id: 6,
      stars: 5,
      title: "So much better than a limo",
      description: "I've rented limos before, but Disco is on another level. Spacious, fun, and actually feels modern. The LED setup and sound system were incredible, and our group had the best night ever. Highly recommend!"
    }
  ];

  // Create duplicated reviews for infinite scroll
  const duplicatedReviews = [...reviews, ...reviews, ...reviews];

  const renderStars = (count: number) => {
    return Array(count)
      .fill(0)
      .map((_, index) => (
        <Star key={index} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
      ));
  };

  const scrollToReview = (index: number) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cardWidth = 320; // Card width + gap
    const containerWidth = container.clientWidth;
    const scrollPosition = (index * cardWidth) - (containerWidth / 2) + (cardWidth / 2);
    
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  const nextReview = () => {
    if (isManualScrollRef.current) return;
    
    const nextIndex = (currentIndex + 1) % reviews.length;
    setCurrentIndex(nextIndex);
    // Use the middle set for smooth infinite scroll
    scrollToReview(nextIndex + reviews.length);
  };

  const startAutoScroll = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    intervalRef.current = setInterval(() => {
      if (!isPaused && !isManualScrollRef.current) {
        nextReview();
      }
    }, 2000);
  };

  const handleManualScroll = () => {
    isManualScrollRef.current = true;
    setIsPaused(true);
    
    // Resume auto scroll after 3 seconds of no manual interaction
    setTimeout(() => {
      isManualScrollRef.current = false;
      setIsPaused(false);
    }, 3000);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  useEffect(() => {
    startAutoScroll();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, currentIndex]);

  useEffect(() => {
    // Initialize scroll position to the middle set
    setTimeout(() => {
      scrollToReview(reviews.length);
    }, 100);
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      className="w-full max-w-full mt-16 mb-8"
    >
      <h3 className="text-white text-2xl font-medium text-center mb-8">What Our Customers Say</h3>
      
      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-4 px-4"
        style={{
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        onScroll={handleManualScroll}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {duplicatedReviews.map((review, index) => (
          <Card 
            key={`${review.id}-${Math.floor(index / reviews.length)}`}
            className="border-none shadow-lg flex-shrink-0 w-80 bg-black/30 backdrop-blur-sm"
            style={{
              scrollSnapAlign: 'center'
            }}
          >
            <CardContent className="p-6">
              <div className="flex mb-2">
                {renderStars(review.stars)}
              </div>
              <h4 className="font-bold text-lg mb-2 text-white">{review.title}</h4>
              <p className="text-white/90 text-sm">{review.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Progress indicators */}
      <div className="flex justify-center mt-4 space-x-2">
        {reviews.map((_, index) => (
          <button
            key={index}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-white' : 'bg-white/30'
            }`}
            onClick={() => {
              setCurrentIndex(index);
              scrollToReview(index + reviews.length);
              handleManualScroll();
            }}
          />
        ))}
      </div>
      
      <style>
        {`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
    </motion.div>
  );
};
